import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { RegistrationService } from '@core/http/registration.service';
import { PoliticaPrivacidad } from '@core/models/Registration';

type LegalPolicyKey = 'TERMINOS_CONDICIONES' | 'PRIVACIDAD' | 'COOKIES';

const LEGAL_POLICY_ITEMS: Array<{ type: LegalPolicyKey; label: string }> = [
  { type: 'PRIVACIDAD', label: 'Politica de Privacidad' },
  { type: 'TERMINOS_CONDICIONES', label: 'Terminos y Condiciones' },
  { type: 'COOKIES', label: 'Politica de Cookies' },
];

@Component({
  selector: 'app-legal-policies-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
  ],
  templateUrl: './legal-policies-dialog.component.html',
  styleUrl: './legal-policies-dialog.component.scss',
})
export class LegalPoliciesDialogComponent implements OnInit {
  loading = true;
  policies: PoliticaPrivacidad[] = [];
  selectedType: LegalPolicyKey = 'PRIVACIDAD';
  selectedPolicy: PoliticaPrivacidad | null = null;
  private policyMap = new Map<LegalPolicyKey, PoliticaPrivacidad>();

  constructor(
    private registrationService: RegistrationService,
    private dialogRef: MatDialogRef<LegalPoliciesDialogComponent>
  ) {}

  ngOnInit(): void {
    this.registrationService.getPrivacyPolicies().subscribe({
      next: (response) => {
        this.policies = (response.data ?? [])
          .filter((item) => this.isTruthy(item.activo))
          .filter((item) => this.isVisibleType(item.tipo));

        this.policyMap.clear();
        for (const policy of this.policies) {
          const type = this.normalizeType(policy.tipo);
          if (!this.policyMap.has(type)) {
            this.policyMap.set(type, policy);
          }
        }

        const initialType = this.policyMap.has('PRIVACIDAD')
          ? 'PRIVACIDAD'
          : LEGAL_POLICY_ITEMS.find((item) => this.policyMap.has(item.type))?.type ?? 'PRIVACIDAD';

        this.selectPolicy(initialType);

        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  selectPolicy(type: LegalPolicyKey): void {
    this.selectedType = type;
    this.selectedPolicy = this.policyMap.get(type) ?? null;
  }

  get visiblePolicyTypes(): Array<{ type: LegalPolicyKey; label: string }> {
    return LEGAL_POLICY_ITEMS.filter((entry) => this.policyMap.has(entry.type));
  }

  private normalizeType(value: string): LegalPolicyKey {
    const normalized = String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    if (normalized.includes('COOKIE')) {
      return 'COOKIES';
    }

    if (normalized.includes('PRIVACIDAD')) {
      return 'PRIVACIDAD';
    }

    if (normalized.includes('TERMINO') || normalized.includes('CONDICION')) {
      return 'TERMINOS_CONDICIONES';
    }

    return 'PRIVACIDAD';
  }

  private isVisibleType(value: string): boolean {
    const normalized = this.normalizeType(value);
    return normalized === 'TERMINOS_CONDICIONES' || normalized === 'PRIVACIDAD' || normalized === 'COOKIES';
  }

  private isTruthy(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }
}
